import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Flashbar, FlashbarProps } from '@cloudscape-design/components';

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationItem {
  id: string;
  type: NotificationType;
  content: string;
  details?: string;
  dismissible: boolean;
}

interface NotificationContextValue {
  addNotification: (type: NotificationType, content: string, details?: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

let notificationCounter = 0;

/**
 * Notification provider using Cloudscape Flashbar.
 * - Success toasts: green, auto-dismiss after 4 seconds.
 * - Error toasts: red, non-dismissing, with "Copy details" button.
 * - Warning/info: amber/blue, dismissible.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const addNotification = useCallback(
    (type: NotificationType, content: string, details?: string) => {
      const id = `notification-${++notificationCounter}`;
      const item: NotificationItem = {
        id,
        type,
        content,
        details,
        dismissible: type !== 'error',
      };

      setNotifications((prev) => [...prev, item]);

      // Auto-dismiss success notifications after 4 seconds
      if (type === 'success') {
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 4000);
      }
    },
    []
  );

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const flashbarItems: FlashbarProps.MessageDefinition[] = notifications.map((n) => ({
    id: n.id,
    type: n.type,
    content: n.content,
    dismissible: true,
    onDismiss: () => {
      setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    },
    ...(n.type === 'error' && n.details
      ? {
          action: (
            <button
              onClick={() => {
                navigator.clipboard.writeText(n.details ?? '');
              }}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: '1px solid currentColor',
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                fontSize: '11px',
                opacity: 0.7,
              }}
            >
              Copy error details
            </button>
          ),
        }
      : {}),
  }));

  return (
    <NotificationContext.Provider value={{ addNotification, clearNotifications }}>
      <Flashbar items={flashbarItems} />
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access the notification system.
 */
export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
